import React, { useState } from "react";
import { ReactComponent as ExcellentSleepIcon } from "../../../../../assects/svg/client_details/SLEEP_CYCLE/Excellent_sleep.svg";
import { ReactComponent as GoodSleepIcon } from "../../../../../assects/svg/client_details/SLEEP_CYCLE/Good_sleep.svg";
import { ReactComponent as FairSleepIcon } from "../../../../../assects/svg/client_details/SLEEP_CYCLE/Fair_sleep.svg";
import { ReactComponent as PoorSleepIcon } from "../../../../../assects/svg/client_details/SLEEP_CYCLE/Poor_sleep.svg";
import { ReactComponent as VeryPoorSleepIcon } from "../../../../../assects/svg/client_details/SLEEP_CYCLE/Very_poor_sleep.svg";
import { ReactComponent as NoSleepIcon } from "../../../../../assects/svg/client_details/SLEEP_CYCLE/No_sleep.svg";
import { ReactComponent as ArrowUpRight } from "../../../../../assects/svg/client_details/ArrowUpRight.svg";
import Drawer from "./Drawer";
import { SleepCycle } from "../../../hooks/useAllLogsData";

interface SleepCardProps {
  sleepCycle: SleepCycle[];
  selectedDate?: string;
}

const SleepCard: React.FC<SleepCardProps> = ({ sleepCycle, selectedDate }) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const getSleepIcon = (mood: string) => {
    const iconMap: { [key: string]: React.FunctionComponent<React.SVGProps<SVGSVGElement>> } = {
      Excellent: ExcellentSleepIcon,
      Good: GoodSleepIcon,
      Fair: FairSleepIcon,
      Poor: PoorSleepIcon,
      "Very Poor": VeryPoorSleepIcon,
    };
    
    return iconMap[mood] || NoSleepIcon;
  };

  const formatTime = (hour: number, minute: number, period: string): string => {
    const paddedHour = hour.toString().padStart(2, "0");
    const paddedMinute = minute.toString().padStart(2, "0");
    return `${paddedHour}:${paddedMinute} ${period}`;
  };

  const calculateDuration = (
    startHour: number,
    startMinute: number,
    startPeriod: string,
    endHour: number,
    endMinute: number,
    endPeriod: string
  ): string => {
    let start24 = startHour === 12 ? 0 : startHour;
    if (startPeriod === "PM") start24 += 12;

    let end24 = endHour === 12 ? 0 : endHour;
    if (endPeriod === "PM") end24 += 12;

    let startTotalMinutes = start24 * 60 + startMinute;
    let endTotalMinutes = end24 * 60 + endMinute;

    if (endTotalMinutes < startTotalMinutes) {
      endTotalMinutes += 24 * 60;
    }

    const durationMinutes = endTotalMinutes - startTotalMinutes;
    const hours = Math.floor(durationMinutes / 60);
    const minutes = durationMinutes % 60;

    return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")} hrs`;
  };

  const renderSleepEntry = (entry: SleepCycle) => {
    const SleepIcon = getSleepIcon(entry.mood);
    const duration = calculateDuration(
      entry.slept_time_hour,
      entry.slept_time_minute,
      entry.slept_time_period,
      entry.awake_time_hour,
      entry.awake_time_minute,
      entry.awake_time_period
    );
    const startTime = formatTime(entry.slept_time_hour, entry.slept_time_minute, entry.slept_time_period);
    const endTime = formatTime(entry.awake_time_hour, entry.awake_time_minute, entry.awake_time_period);
    
    return (
      <div className="flex items-center gap-2">
        <SleepIcon className="w-6 h-6 flex-shrink-0" />
        <div className="flex-1">
          <p className="text-[12px] 3xl:text-sm font-medium font-inter text-[#1F1F1F]">
            {entry.mood}
          </p>
          <p className="text-[10px] 3xl:text-[12px] font-medium font-inter text-[#8D8D8D]">
            <span className="text-[10px] 3xl:text-[12px] font-medium font-inter text-[#8D8D8D]">
              {duration}
            </span>
            {" "} | {startTime} - {endTime}
          </p>
        </div>
        <span 
          className={`ml-auto px-2 py-0.5 rounded-md text-[10px] 3xl:text-xs font-medium flex-shrink-0 ${
            entry.type === "nap" 
              ? "bg-[#5E90D01A] border-[0.5px] border-[#5E90D0] text-[#5E90D0]" 
              : "bg-[#8763DA1A] text-[#8763DA] border-[0.5px] border-[#8763DA]"
          }`}
        >
          {entry.type.charAt(0).toUpperCase() + entry.type.slice(1)}
        </span>
      </div>
    );
  };

  const displayedEntries = sleepCycle.slice(0, 2);

  if (!sleepCycle || sleepCycle.length === 0) {
    return (
      <div className="bg-white rounded-md shadow-sm p-4 flex flex-col h-full">
        <h3 className="text-[12px] 3xl:text-sm font-semibold font-inter text-[#1F1F1F] uppercase mb-4">
          SLEEP CYCLE
        </h3>
        <div className="flex-1 flex items-center justify-center min-h-[92px] pb-4">
          <p className="text-sm font-medium text-[#646464] font-inter">No entries yet</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div onClick={() => setIsDrawerOpen(true)} className="bg-white rounded-md shadow-sm p-4 flex flex-col h-full cursor-pointer">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-[12px] 3xl:text-sm font-semibold font-inter text-[#1F1F1F] uppercase">
            SLEEP CYCLE
          </h3>
          <button
            
            className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="View all sleep entries"
          >
            <ArrowUpRight className="w-4 h-4 3xl:w-5 3xl:h-5 text-[#1F1F1F]" />
          </button>
        </div>
        
        <div className="flex-1 min-h-[92px]">
          {displayedEntries.map((entry, index) => (
            <React.Fragment key={index}>
              {renderSleepEntry(entry)}
              {index < displayedEntries.length - 1 && (
                <div className="w-full border-t border-gray-200 my-2.5"></div>
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      <Drawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        title="Sleep Cycle"
        selectedDate={selectedDate}
      >
        <div className="space-y-3">
          {sleepCycle.map((entry, index) => (
            <div key={index} className="pb-3 border-b border-gray-100">
              {renderSleepEntry(entry)}
            </div>
          ))}
        </div>
      </Drawer>
    </>
  );
};

export default SleepCard;